import {
  visibleNavItems as visibleNavItems_,
  NAV_GROUP_ORDER,
} from '@components/Common/Utils/NavigationItems';
import { routePath } from '@components/Common/Utils/Routes';
import { useMediaQuery } from '@components/Common/DesktopOnly';
import { formatRole } from '@components/Common/Utils/roles';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined';
import SettingsBrightnessOutlinedIcon from '@mui/icons-material/SettingsBrightnessOutlined';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import {
  Avatar,
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { logoutUserProfileRequest } from '@store/features/loginUserProfile/actions';
import React, { useState } from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Dispatch } from 'redux';
import { useThemeMode, type ThemePreference } from '../../theme/ThemeModeProvider';
import './index.scss';

import type { RootState } from '@store/rootReducer';

const mapStateToProps = (state: RootState) => ({
  loginUserProfile: state.loginUserProfile.profile,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  logoutUserProfileRequest: () => dispatch(logoutUserProfileRequest()),
});

const THEME_CHOICES: { value: ThemePreference; label: string; icon: React.ReactNode }[] = [
  { value: 'light', label: 'Light', icon: <LightModeOutlinedIcon fontSize="small" /> },
  { value: 'dark', label: 'Dark', icon: <DarkModeOutlinedIcon fontSize="small" /> },
  { value: 'system', label: 'System', icon: <SettingsBrightnessOutlinedIcon fontSize="small" /> },
];

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const SidebarPresentation: React.FC<PropsFromRedux> = ({
  loginUserProfile,
  logoutUserProfileRequest,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { preference, setPreference } = useThemeMode();

  // 768–1023px collapses the rail to icons only, so the label has to come back
  // as a tooltip — an unlabelled icon rail is a memory test.
  const isIconRail = useMediaQuery('(min-width: 768px) and (max-width: 1023px)');

  // RBAC: dedicated-HR users don't see Super-Admin oversight items; Super Admins
  // don't see HR-only items. Grouping is purely presentational — filtering is unchanged.
  const visibleNavItems = visibleNavItems_(loginUserProfile?.role);

  const isRoutePathMatch = (current: string, actual: string) =>
    current.split('/')[1] === actual.split('/')[1];

  const firstName = loginUserProfile?.personalDetails?.firstName || '';
  const lastName = loginUserProfile?.personalDetails?.lastName || '';
  const initials = `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase();
  const fullName = `${firstName} ${lastName}`.trim() || 'User';
  const email = loginUserProfile?.personalDetails?.email || '';
  const roleLabel = formatRole(loginUserProfile?.role);
  const menuOpen = Boolean(anchorEl);

  const handleProfile = () => {
    navigate('/user-details');
    setAnchorEl(null);
  };
  const handleLogout = () => {
    logoutUserProfileRequest();
    navigate(routePath('Login'));
    setAnchorEl(null);
  };

  return (
    <Box className="cc-sidebar" component="nav" aria-label="Primary">
      {/* Header — brand mark + wordmark */}
      <Box className="cc-sidebar__header">
        <Box className="cc-sidebar__brand-tile">
          <img src="/mintly-logo.png" alt="Mintly" />
        </Box>
        <Box className="cc-sidebar__brand-text">
          <Typography component="span" className="cc-sidebar__wordmark">
            Mintly
          </Typography>
          <Typography component="span" className="cc-sidebar__subbrand">
            Control Center
          </Typography>
        </Box>
      </Box>

      {/* Grouped nav */}
      <Box className="cc-sidebar__nav">
        {NAV_GROUP_ORDER.map((group) => {
          const items = visibleNavItems.filter((i) => i.group === group);
          if (items.length === 0) return null; // hide empty groups
          return (
            <Box key={group} className="cc-sidebar__group">
              <Typography component="div" className="cc-sidebar__group-label">
                {group}
              </Typography>
              {items.map(({ id, icon, label, path, roleTag }) => {
                const active = isRoutePathMatch(location.pathname, path);
                const row = (
                  <button
                    key={id}
                    type="button"
                    className={`cc-nav-row${active ? ' is-active' : ''}`}
                    onClick={() => navigate(path)}
                    aria-current={active ? 'page' : undefined}
                    aria-label={label}
                  >
                    <span className="cc-nav-row__icon">{icon}</span>
                    <span className="cc-nav-row__label">{label}</span>
                    {roleTag && (
                      <span className="cc-nav-row__tag">{roleTag}</span>
                    )}
                  </button>
                );
                return isIconRail ? (
                  <Tooltip key={id} title={label} placement="right">
                    {row}
                  </Tooltip>
                ) : (
                  row
                );
              })}
            </Box>
          );
        })}
      </Box>

      {/* Footer — user. A real button, so Enter and Space open it like any other control. */}
      <button
        type="button"
        className="cc-sidebar__footer"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-controls={menuOpen ? 'cc-acctmenu' : undefined}
        aria-label={`Account: ${fullName}, ${roleLabel}`}
      >
        <Avatar className="cc-sidebar__avatar">{initials || 'U'}</Avatar>
        <Box className="cc-sidebar__user">
          <Typography component="span" className="cc-sidebar__user-name">
            {fullName}
          </Typography>
          <Typography component="span" className="cc-sidebar__user-role">
            {roleLabel}
          </Typography>
        </Box>
        <UnfoldMoreIcon className="cc-sidebar__footer-icon" />
      </button>
      <Menu
        id="cc-acctmenu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => setAnchorEl(null)}
        /*
         * Directly above the user block, aligned to its left edge. Anchoring to the button's
         * top-right put the menu over whichever page panel was underneath, where it read as a
         * popup belonging to the content rather than to the account control that opened it.
         */
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        /* The rail IS the window edge here, so MUI's default 16px margin has nothing to protect. */
        marginThreshold={0}
        slotProps={{
          paper: { className: 'cc-sidebar__menu' },
          list: { 'aria-label': 'Account' },
        }}
      >
        {/* Who you are signed in as: the one thing the old menu's "Role: ..." line half-said. */}
        <Box className="cc-acctmenu__head" component="li" role="presentation">
          <Avatar className="cc-acctmenu__avatar">{initials || 'U'}</Avatar>
          <Box className="cc-acctmenu__who">
            <Typography component="span" className="cc-acctmenu__name">{fullName}</Typography>
            {email && (
              <Typography component="span" className="cc-acctmenu__email" title={email}>{email}</Typography>
            )}
            <span className="cc-acctmenu__role">{roleLabel}</span>
          </Box>
        </Box>
        <Divider component="li" className="cc-acctmenu__divider" />
        <MenuItem onClick={handleProfile} className="cc-acctmenu__item">
          <ListItemIcon><PersonOutlineIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Your profile" secondary="Name, password" />
        </MenuItem>
        {/* Theme in one tap, rather than a trip to the profile page. Same three choices as there. */}
        <Box className="cc-acctmenu__theme" component="li" role="presentation">
          <span className="cc-acctmenu__theme-label" id="cc-acctmenu-theme-label">Theme</span>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={preference}
            onChange={(_, next: ThemePreference | null) => next && setPreference(next)}
            aria-labelledby="cc-acctmenu-theme-label"
            className="cc-acctmenu__theme-group"
          >
            {THEME_CHOICES.map((c) => (
              <Tooltip key={c.value} title={c.label}>
                <ToggleButton value={c.value} aria-label={c.label}>{c.icon}</ToggleButton>
              </Tooltip>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Divider component="li" className="cc-acctmenu__divider" />
        <MenuItem onClick={handleLogout} className="cc-acctmenu__item">
          <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Sign out" />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default connector(SidebarPresentation);
